import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { downloadFile, formatINR, formatDate } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaymentBadge, DeliveryStatusBadge } from "@/components/badges";
import { FileSpreadsheet, FileText, Printer, Truck, Wallet, MapPin, Phone, Loader2 } from "lucide-react";

function SummaryTile({ label, value, tone }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-mono text-lg font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function CustomerLedgerSheet({ customer, open, onClose }) {
  const cid = customer?.id;
  const { data, isLoading } = useQuery({
    queryKey: ["ledger", cid],
    queryFn: async () => (await api.get(`/customers/${cid}/ledger`)).data,
    enabled: !!cid && open,
  });

  const doExport = async (fmt) => {
    try {
      await downloadFile(`/customers/${cid}/ledger/export?format=${fmt}`, `ledger_${customer.name}.${fmt === "pdf" ? "pdf" : "xlsx"}`);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent id="customer360-print" className="flex w-full flex-col overflow-hidden p-0 sm:max-w-2xl" data-testid="ledger-sheet">
        <SheetHeader className="border-b p-5">
          <SheetTitle className="font-display text-xl">{customer?.name}</SheetTitle>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{customer?.phone || "—"}</span>
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{customer?.area || "—"}</span>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading || !data ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <SummaryTile label="Billed" value={formatINR(data.summary.total_billed)} />
                <SummaryTile label="Collected" value={formatINR(data.summary.total_paid)} tone="text-emerald-600 dark:text-emerald-400" />
                <SummaryTile label="Outstanding" value={formatINR(data.summary.outstanding)} tone={data.summary.outstanding > 0 ? "text-rose-600 dark:text-rose-400" : ""} />
                <SummaryTile label="Deliveries" value={data.summary.deliveries} />
                <SummaryTile label="Returns" value={`${data.summary.returns} · ${formatINR(data.summary.return_value)}`} tone={data.summary.returns > 0 ? "text-orange-600 dark:text-orange-400" : ""} />
              </div>

              {data.returns?.length > 0 && (
                <div className="mt-5">
                  <h4 className="mb-2 font-display text-sm font-semibold">Return / Mismatch Stock</h4>
                  <div className="space-y-2">
                    {data.returns.map((r, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm dark:border-orange-900 dark:bg-orange-950/30" data-testid={`ledger-return-${i}`}>
                        <span className="font-mono text-xs text-muted-foreground">{formatDate(r.date)}</span>
                        <span className="font-medium">{r.reason}</span>
                        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{r.description} · {r.invoices}</span>
                        <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">{r.action}{r.action === "Send back" ? (r.sent_back ? " · sent" : " · pending") : ""}</span>
                        <span className="font-mono font-semibold text-orange-700 dark:text-orange-300">{formatINR(r.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-center justify-between">
                <h4 className="font-display text-sm font-semibold">Transaction History</h4>
                <div className="print-hidden flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="ledger-print"><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
                  <Button variant="outline" size="sm" onClick={() => doExport("excel")} data-testid="ledger-export-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
                  <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid="ledger-export-pdf"><FileText className="mr-1.5 h-4 w-4" /> PDF</Button>
                </div>
              </div>

              {data.events.length === 0 ? (
                <p className="py-12 text-center text-sm text-muted-foreground">No transactions yet</p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/60">
                        <TableHead>Date</TableHead>
                        <TableHead>Detail</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.events.map((e, i) => (
                        <TableRow key={i} className="align-top" data-testid={`ledger-row-${i}`}>
                          <TableCell className="whitespace-nowrap font-mono text-xs">{formatDate(e.date)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {e.type === "delivery" ? <Truck className="h-3.5 w-3.5 text-muted-foreground" /> : <Wallet className="h-3.5 w-3.5 text-muted-foreground" />}
                              <span className="text-sm font-medium">{e.description}</span>
                              {e.type === "payment" && e.mode && <PaymentBadge mode={e.mode} />}
                              {e.type === "delivery" && e.status && <DeliveryStatusBadge status={e.status} />}
                            </div>
                            {e.ref && <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{e.ref}</div>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{e.debit ? formatINR(e.debit) : "—"}</TableCell>
                          <TableCell className="text-right font-mono text-sm text-emerald-600 dark:text-emerald-400">{e.credit ? formatINR(e.credit) : "—"}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">{formatINR(e.balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
